import { StationsInputMui } from '@app/components/stations/StationsInputMui';
import { useAppData } from '@app/core/app-data';
import { useToast } from '@app/core/notifications';
import { isVideoStation } from '@app/core/stations/helpers';
import { useOwnedStations } from '@app/core/stations/hooks/useOwnedStations';
import { WatermarkConfig } from '@app/core/types/stations';
import { zodResolver } from '@hookform/resolvers/zod';
import { Box } from '@mui/material';
import { useMemo, useState } from 'react';
import { FormContainer } from 'react-hook-form-mui';
import { useIntl } from 'react-intl';
import { apiClient } from '../../../../../api';
import { MediaWatermarkFormContent } from './media-watermark/components/MediaWatermarkFormContent';
import { getSchema, MediaWatermarkFormValues } from './media-watermark/types';

/** Station-scoped form to configure the media watermark (RHF + MUI). */
export function MediaWatermarkForm() {
  const { refetchStations } = useAppData();
  const stations = useOwnedStations(isVideoStation);
  const [station, setStation] = useState(stations[0]);
  const { $t } = useIntl();
  const { notifySuccess } = useToast();
  const schema = useMemo(() => getSchema((msg) => $t(msg)), [$t]);

  const config = station.featuresConfig?.fragments?.mediaWatermark || ({} as WatermarkConfig);

  const defaultValues: MediaWatermarkFormValues = {
    enabled: config.enabled ?? true,
    imageUrl: config.imageUrl ?? '',
    positionX: (config.positionX as MediaWatermarkFormValues['positionX']) ?? 'left',
    paddingX: config.paddingX ?? 10,
    positionY: (config.positionY as MediaWatermarkFormValues['positionY']) ?? 'top',
    paddingY: config.paddingY ?? 10,
    opacity: config.opacity ?? 1,
  };

  return (
    <Box>
      <Box mb={3} maxWidth={400}>
        <StationsInputMui
          stations={stations}
          value={station}
          onChange={setStation}
          selectProps={{
            size: 'small',
          }}
        />
      </Box>

      <FormContainer
        key={station.id}
        defaultValues={defaultValues}
        resolver={zodResolver(schema)}
        onSuccess={async (data) => {
          await apiClient.fragments.configUpdate(station.id, {
            mediaWatermark: data,
          });

          notifySuccess(
            $t(
              {
                id: 'admin.fragments.fragmentsConfigUpdated',
                defaultMessage: 'Configuración de fragmentos para {stationName} actualizada',
              },
              { stationName: station.name },
            ),
          );

          refetchStations();
        }}
      >
        <MediaWatermarkFormContent />
      </FormContainer>
    </Box>
  );
}
